import React, { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../services/authService'
import toast from 'react-hot-toast'
import {
  ChatBubbleLeftRightIcon, DocumentTextIcon, ChartBarIcon, PaperAirplaneIcon,
  MagnifyingGlassIcon, PlusIcon, PencilIcon, TrashIcon, XMarkIcon, CheckIcon,
  ClockIcon, SignalIcon, SignalSlashIcon, UserCircleIcon, BuildingOfficeIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'

const TEMPLATE_CATEGORIES = [
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'proposta', label: 'Proposta' },
  { value: 'boas_vindas', label: 'Boas-vindas' },
  { value: 'reativacao', label: 'Reativacao' },
  { value: 'cobranca', label: 'Cobranca' },
  { value: 'contrato', label: 'Contrato' },
  { value: 'pos_venda', label: 'Pos-venda' }
]

const TEMPLATE_VARIABLES = [
  { key: '{{nome_contato}}', label: 'Nome do Contato' },
  { key: '{{nome_empresa}}', label: 'Nome da Empresa' },
  { key: '{{valor_proposta}}', label: 'Valor da Proposta' },
  { key: '{{data_vencimento}}', label: 'Data de Vencimento' },
  { key: '{{numero_contrato}}', label: 'Numero do Contrato' },
  { key: '{{nome_veiculo}}', label: 'Nome do Veiculo' },
  { key: '{{link_boleto}}', label: 'Link do Boleto' }
]

const MarketingWhatsappPage = () => {
  const [activeTab, setActiveTab] = useState('chat')
  const [connectionStatus, setConnectionStatus] = useState({ connected: false, phone_number: null })

  const [conversations, setConversations] = useState([])
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [conversationSearch, setConversationSearch] = useState('')
  const [loadingConversations, setLoadingConversations] = useState(false)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [sendingMessage, setSendingMessage] = useState(false)
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false)
  const [quickTemplates, setQuickTemplates] = useState([])

  const [templates, setTemplates] = useState([])
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [templateForm, setTemplateForm] = useState({
    name: '', slug: '', category: 'follow_up', content: '', is_active: true
  })

  const [metrics, setMetrics] = useState(null)
  const [loadingMetrics, setLoadingMetrics] = useState(false)
  const messagesEndRef = useRef(null)
  const templateDropdownRef = useRef(null)

  const checkConnectionStatus = useCallback(async () => {
    try {
      const response = await api.get('/api/marketing/whatsapp/status')
      setConnectionStatus(response.data || { connected: false, phone_number: null })
    } catch {
      setConnectionStatus({ connected: false, phone_number: null })
    }
  }, [])

  useEffect(() => {
    checkConnectionStatus()
  }, [checkConnectionStatus])

  useEffect(() => {
    if (activeTab === 'chat') {
      loadConversations()
      loadQuickTemplates()
    } else if (activeTab === 'templates') {
      loadTemplates()
    } else if (activeTab === 'metricas') {
      loadMetrics()
    }
  }, [activeTab])

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target)) {
        setShowTemplateDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // === Chat functions ===
  const loadConversations = async () => {
    setLoadingConversations(true)
    try {
      const response = await api.get('/api/marketing/whatsapp/conversations')
      setConversations(response.data?.data || response.data || [])
    } catch (error) {
      console.error('Erro ao carregar conversas:', error)
      toast.error('Erro ao carregar conversas')
    } finally {
      setLoadingConversations(false)
    }
  }

  const loadQuickTemplates = async () => {
    try {
      const response = await api.get('/api/marketing/whatsapp/templates')
      const all = response.data?.data || response.data || []
      setQuickTemplates(all.filter(t => t.is_active))
    } catch {
      // silent - templates are optional for chat
    }
  }

  const selectConversation = async (conversation) => {
    setSelectedConversation(conversation)
    setLoadingMessages(true)
    try {
      const phone = conversation.phone || conversation.contact_phone
      const response = await api.get(`/api/marketing/whatsapp/conversations/${encodeURIComponent(phone)}/messages`)
      setMessages(response.data?.data || response.data || [])
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error)
      toast.error('Erro ao carregar mensagens')
      setMessages([])
    } finally {
      setLoadingMessages(false)
    }
  }

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return

    const phone = selectedConversation.phone || selectedConversation.contact_phone
    setSendingMessage(true)
    try {
      await api.post('/api/marketing/whatsapp/send', {
        phone,
        message: newMessage.trim()
      })
      setNewMessage('')
      // Reload messages
      const response = await api.get(`/api/marketing/whatsapp/conversations/${encodeURIComponent(phone)}/messages`)
      setMessages(response.data?.data || response.data || [])
      loadConversations()
      toast.success('Mensagem enviada')
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error)
      toast.error(error.response?.data?.message || 'Erro ao enviar mensagem')
    } finally {
      setSendingMessage(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const applyQuickTemplate = (tpl) => {
    let content = (tpl.content || '')
      .replace('{{nome_contato}}', selectedConversation?.contact_name || '')
      .replace('{{nome_empresa}}', selectedConversation?.company_name || '')
    setNewMessage(content)
    setShowTemplateDropdown(false)
  }

  const filteredConversations = conversations.filter(c => {
    if (!conversationSearch) return true
    const s = conversationSearch.toLowerCase()
    return (c.contact_name || c.phone || '').toLowerCase().includes(s) || (c.company_name || '').toLowerCase().includes(s)
  })

  const statusBadgeMap = {
    open: { cls: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400', label: 'Aberta' },
    waiting: { cls: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400', label: 'Aguardando' },
    resolved: { cls: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300', label: 'Resolvida' }
  }
  const getConversationStatusBadge = (status) => {
    const cfg = statusBadgeMap[status] || statusBadgeMap.open
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>{cfg.label}</span>
  }

  const getMessageStatusIcon = (status) => {
    const map = {
      read: <span className="text-blue-400 text-xs ml-1" title="Lida">&#10003;&#10003;</span>,
      delivered: <span className="text-gray-400 text-xs ml-1" title="Entregue">&#10003;&#10003;</span>,
      sent: <span className="text-gray-400 text-xs ml-1" title="Enviada">&#10003;</span>,
      failed: <span className="text-red-400 text-xs ml-1" title="Falha">!</span>
    }
    return map[status] || <ClockIcon className="w-3 h-3 text-gray-400 inline ml-1" title="Pendente" />
  }

  const formatMessageTime = (ts) => {
    if (!ts) return ''
    const d = new Date(ts), now = new Date(), diff = Math.floor((now - d) / 86400000)
    const t = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    if (diff === 0) return t
    if (diff === 1) return `Ontem ${t}`
    if (diff < 7) return `${d.toLocaleDateString('pt-BR', { weekday: 'short' })} ${t}`
    return `${d.toLocaleDateString('pt-BR')} ${t}`
  }

  // === Template functions ===
  const loadTemplates = async () => {
    setLoadingTemplates(true)
    try {
      const response = await api.get('/api/marketing/whatsapp/templates')
      setTemplates(response.data?.data || response.data || [])
    } catch (error) {
      console.error('Erro ao carregar templates:', error)
      toast.error('Erro ao carregar templates')
    } finally {
      setLoadingTemplates(false)
    }
  }

  const generateSlug = (name) =>
    name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')

  const handleTemplateNameChange = (value) =>
    setTemplateForm(prev => ({ ...prev, name: value, slug: generateSlug(value) }))

  const openTemplateModal = (template = null) => {
    if (template) {
      setEditingTemplate(template)
      setTemplateForm({
        name: template.name || '',
        slug: template.slug || '',
        category: template.category || 'follow_up',
        content: template.content || '',
        is_active: template.is_active !== false
      })
    } else {
      setEditingTemplate(null)
      setTemplateForm(defaultTemplateForm)
    }
    setShowTemplateModal(true)
  }

  const defaultTemplateForm = { name: '', slug: '', category: 'follow_up', content: '', is_active: true }

  const closeTemplateModal = () => {
    setShowTemplateModal(false)
    setEditingTemplate(null)
    setTemplateForm(defaultTemplateForm)
  }

  const saveTemplate = async () => {
    if (!templateForm.name.trim() || !templateForm.content.trim()) {
      toast.error('Preencha o nome e o conteudo do template')
      return
    }

    try {
      const payload = {
        ...templateForm,
        slug: templateForm.slug || generateSlug(templateForm.name)
      }

      if (editingTemplate) {
        await api.put(`/api/marketing/whatsapp/templates/${editingTemplate.id}`, payload)
        toast.success('Template atualizado com sucesso')
      } else {
        await api.post('/api/marketing/whatsapp/templates', payload)
        toast.success('Template criado com sucesso')
      }
      closeTemplateModal()
      loadTemplates()
    } catch (error) {
      console.error('Erro ao salvar template:', error)
      toast.error(error.response?.data?.message || 'Erro ao salvar template')
    }
  }

  const deleteTemplate = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este template?')) return
    try {
      await api.delete(`/api/marketing/whatsapp/templates/${id}`)
      toast.success('Template excluido')
      loadTemplates()
    } catch (error) {
      console.error('Erro ao excluir template:', error)
      toast.error('Erro ao excluir template')
    }
  }

  const toggleTemplateActive = async (template) => {
    try {
      await api.put(`/api/marketing/whatsapp/templates/${template.id}`, {
        ...template,
        is_active: !template.is_active
      })
      loadTemplates()
    } catch (error) {
      console.error('Erro ao alterar status:', error)
      toast.error('Erro ao alterar status do template')
    }
  }

  const insertVariable = (v) => setTemplateForm(prev => ({ ...prev, content: prev.content + v }))
  const getCategoryLabel = (v) => TEMPLATE_CATEGORIES.find(c => c.value === v)?.label || v

  // === Metrics functions ===
  const loadMetrics = async () => {
    setLoadingMetrics(true)
    try {
      const response = await api.get('/api/marketing/whatsapp/metrics')
      setMetrics(response.data?.data || response.data || null)
    } catch (error) {
      console.error('Erro ao carregar metricas:', error)
      toast.error('Erro ao carregar metricas')
    } finally {
      setLoadingMetrics(false)
    }
  }

  const renderConnectionBanner = () => {
    const on = connectionStatus.connected
    return (
      <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border ${
        on ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800'
           : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800'
      }`}>
        <span className="relative flex h-2.5 w-2.5">
          {on && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />}
          <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${on ? 'bg-green-500' : 'bg-red-500'}`} />
        </span>
        {on ? <SignalIcon className="w-4 h-4" /> : <SignalSlashIcon className="w-4 h-4" />}
        <span>{on ? 'WhatsApp Conectado' : 'WhatsApp Desconectado'}</span>
        {on && connectionStatus.phone_number && (
          <span className="text-green-600 dark:text-green-300 ml-1">({connectionStatus.phone_number})</span>
        )}
        <button onClick={checkConnectionStatus} className="ml-auto p-1 rounded hover:bg-white/30 dark:hover:bg-white/10 transition-colors" title="Verificar conexao">
          <ArrowPathIcon className="w-4 h-4" />
        </button>
      </div>
    )
  }

  const renderChatTab = () => (
    <div className="flex h-[calc(100vh-260px)] min-h-[500px] border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800">
      <div className="w-80 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 flex flex-col">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar conversa..."
              value={conversationSearch}
              onChange={(e) => setConversationSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loadingConversations ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="text-center text-gray-500 dark:text-gray-400 py-12 px-4">
              <ChatBubbleLeftRightIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Nenhuma conversa encontrada</p>
            </div>
          ) : (
            filteredConversations.map((conv, idx) => {
              const phone = conv.phone || conv.contact_phone
              const isSelected = selectedConversation && (selectedConversation.phone || selectedConversation.contact_phone) === phone
              return (
                <button
                  key={phone || idx}
                  onClick={() => selectConversation(conv)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                    isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-l-blue-500' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <UserCircleIcon className="w-9 h-9 text-gray-400 dark:text-gray-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                          {conv.contact_name || phone}
                        </span>
                        {conv.unread_count > 0 && (
                          <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-500 text-white text-xs font-bold">
                            {conv.unread_count > 9 ? '9+' : conv.unread_count}
                          </span>
                        )}
                      </div>
                      {conv.company_name && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <BuildingOfficeIcon className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{conv.company_name}</span>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                        {conv.last_message_preview || 'Sem mensagens'}
                      </p>
                      <div className="flex items-center justify-between mt-1">
                        {getConversationStatusBadge(conv.status)}
                        {conv.last_message_at && (
                          <span className="text-xs text-gray-400">
                            {formatMessageTime(conv.last_message_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              )
            })
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        {!selectedConversation ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 dark:text-gray-500">
            <ChatBubbleLeftRightIcon className="w-16 h-16 mb-4 opacity-30" />
            <p className="text-lg font-medium">Selecione uma conversa</p>
            <p className="text-sm mt-1">Escolha uma conversa no painel lateral para visualizar as mensagens</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <UserCircleIcon className="w-9 h-9 text-gray-400 dark:text-gray-500" />
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                  {selectedConversation.contact_name || selectedConversation.phone || selectedConversation.contact_phone}
                </h3>
                <div className="flex items-center gap-2">
                  {selectedConversation.company_name && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">{selectedConversation.company_name}</span>
                  )}
                  <span className="text-xs text-gray-400">
                    {selectedConversation.phone || selectedConversation.contact_phone}
                  </span>
                </div>
              </div>
              {getConversationStatusBadge(selectedConversation.status)}
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50 dark:bg-gray-900/30">
              {loadingMessages ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center text-gray-400 dark:text-gray-500 py-12">
                  <p className="text-sm">Nenhuma mensagem nesta conversa</p>
                </div>
              ) : (
                messages.map((msg, idx) => {
                  const isOutbound = msg.direction === 'outbound' || msg.type === 'outbound'
                  return (
                    <div key={msg.id || idx} className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[70%] rounded-2xl px-4 py-2 shadow-sm ${
                          isOutbound
                            ? 'bg-blue-600 text-white rounded-br-md'
                            : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-bl-md border border-gray-200 dark:border-gray-600'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap break-words">{msg.content || msg.body || msg.message}</p>
                        <div className={`flex items-center justify-end gap-1 mt-1 ${
                          isOutbound ? 'text-blue-200' : 'text-gray-400'
                        }`}>
                          <span className="text-xs">{formatMessageTime(msg.created_at || msg.timestamp)}</span>
                          {isOutbound && getMessageStatusIcon(msg.status)}
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
              <div className="flex items-end gap-2">
                <div className="relative" ref={templateDropdownRef}>
                  <button
                    onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                    className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="Templates rapidos"
                  >
                    <DocumentTextIcon className="w-5 h-5" />
                  </button>
                  {showTemplateDropdown && quickTemplates.length > 0 && (
                    <div className="absolute bottom-12 left-0 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-20 max-h-64 overflow-y-auto">
                      <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Templates rapidos</span>
                      </div>
                      {quickTemplates.map(t => (
                        <button
                          key={t.id}
                          onClick={() => applyQuickTemplate(t)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors border-b border-gray-50 dark:border-gray-700/30 last:border-b-0"
                        >
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.name}</span>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{t.content}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Digite sua mensagem..."
                  rows={1}
                  className="flex-1 resize-none border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-2 text-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none max-h-28 overflow-y-auto"
                  style={{ minHeight: '40px' }}
                  onInput={(e) => {
                    e.target.style.height = 'auto'
                    e.target.style.height = Math.min(e.target.scrollHeight, 112) + 'px'
                  }}
                />

                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || sendingMessage}
                  className="p-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white rounded-xl transition-colors disabled:cursor-not-allowed"
                  title="Enviar mensagem"
                >
                  {sendingMessage ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <PaperAirplaneIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )

  const renderTemplatesTab = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Gerencie os templates de mensagens do WhatsApp para campanhas e atendimento.
        </p>
        <button
          onClick={() => openTemplateModal()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Novo Template
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        {loadingTemplates ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <DocumentTextIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p>Nenhum template cadastrado</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Nome</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Categoria</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Conteudo</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Ativo</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-600 dark:text-gray-300">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {templates.map(template => (
                <tr key={template.id} className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                  <td className="px-4 py-3">
                    <div>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{template.name}</span>
                      {template.slug && (
                        <p className="text-xs text-gray-400 mt-0.5">slug: {template.slug}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                      {getCategoryLabel(template.category)}
                    </span>
                  </td>
                  <td className="px-4 py-3 max-w-xs">
                    <p className="text-gray-600 dark:text-gray-300 truncate" title={template.content}>
                      {template.content?.substring(0, 80)}{template.content?.length > 80 ? '...' : ''}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => toggleTemplateActive(template)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        template.is_active ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transform transition-transform ${
                        template.is_active ? 'translate-x-4' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openTemplateModal(template)}
                        className="p-1.5 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteTemplate(template.id)}
                        className="p-1.5 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Excluir"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingTemplate ? 'Editar Template' : 'Novo Template'}
              </h3>
              <button
                onClick={closeTemplateModal}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome</label>
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => handleTemplateNameChange(e.target.value)}
                  placeholder="Ex: Follow-up Proposta Comercial"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Slug (automatico)</label>
                <input
                  type="text"
                  value={templateForm.slug}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, slug: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Categoria</label>
                <select
                  value={templateForm.category}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  {TEMPLATE_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Conteudo da Mensagem</label>
                <textarea
                  value={templateForm.content}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Ola {{nome_contato}}, tudo bem? Aqui e da VIPLOC..."
                  rows={5}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                />
                {/* Variable helper buttons */}
                <div className="mt-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">Inserir variavel:</span>
                  <div className="flex flex-wrap gap-1.5">
                    {TEMPLATE_VARIABLES.map(v => (
                      <button
                        key={v.key}
                        type="button"
                        onClick={() => insertVariable(v.key)}
                        className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 border border-gray-200 dark:border-gray-600 transition-colors"
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Template ativo</label>
                <button
                  type="button"
                  onClick={() => setTemplateForm(prev => ({ ...prev, is_active: !prev.is_active }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    templateForm.is_active ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${
                    templateForm.is_active ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={closeTemplateModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={saveTemplate}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                <CheckIcon className="w-4 h-4" />
                {editingTemplate ? 'Salvar Alteracoes' : 'Criar Template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  const renderMetricsTab = () => {
    const overview = metrics?.overview || {}
    const byDay = metrics?.by_day || []

    const kpis = [
      { label: 'Total Mensagens', value: overview.total || 0, color: 'bg-blue-500', textColor: 'text-blue-600 dark:text-blue-400', bgLight: 'bg-blue-50 dark:bg-blue-900/20' },
      { label: 'Enviadas', value: overview.sent || 0, color: 'bg-indigo-500', textColor: 'text-indigo-600 dark:text-indigo-400', bgLight: 'bg-indigo-50 dark:bg-indigo-900/20' },
      { label: 'Recebidas', value: overview.received || 0, color: 'bg-purple-500', textColor: 'text-purple-600 dark:text-purple-400', bgLight: 'bg-purple-50 dark:bg-purple-900/20' },
      { label: 'Entregues', value: overview.delivered || 0, color: 'bg-green-500', textColor: 'text-green-600 dark:text-green-400', bgLight: 'bg-green-50 dark:bg-green-900/20' },
      { label: 'Lidas', value: overview.read || 0, color: 'bg-cyan-500', textColor: 'text-cyan-600 dark:text-cyan-400', bgLight: 'bg-cyan-50 dark:bg-cyan-900/20' },
      { label: 'Falhas', value: overview.failed || 0, color: 'bg-red-500', textColor: 'text-red-600 dark:text-red-400', bgLight: 'bg-red-50 dark:bg-red-900/20' }
    ]

    const maxDayValue = byDay.length > 0 ? Math.max(...byDay.map(d => d.count || d.total || 0), 1) : 1

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {kpis.map((kpi, idx) => (
            <div key={idx} className={`${kpi.bgLight} border border-gray-200 dark:border-gray-700 rounded-xl p-4`}>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{kpi.label}</p>
              <p className={`text-2xl font-bold mt-1 ${kpi.textColor}`}>
                {Number(kpi.value).toLocaleString('pt-BR')}
              </p>
            </div>
          ))}
        </div>

        {overview.total > 0 && (() => {
          const rates = [
            { label: 'Taxa de Entrega', pct: overview.sent > 0 ? (overview.delivered / overview.sent) * 100 : 0, color: 'green' },
            { label: 'Taxa de Leitura', pct: overview.delivered > 0 ? (overview.read / overview.delivered) * 100 : 0, color: 'cyan' },
            { label: 'Taxa de Falha', pct: overview.sent > 0 ? (overview.failed / overview.sent) * 100 : 0, color: 'red' }
          ]
          return (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Taxa de Entrega e Leitura</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {rates.map((r, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{r.label}</span>
                      <span className={`text-sm font-bold text-${r.color}-600 dark:text-${r.color}-400`}>{r.pct.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div className={`bg-${r.color}-500 h-2 rounded-full transition-all duration-500`} style={{ width: `${r.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        })()}

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Mensagens por Dia</h3>
          {loadingMetrics ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : byDay.length === 0 ? (
            <div className="text-center py-8 text-gray-400 dark:text-gray-500">
              <ChartBarIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Sem dados para exibir</p>
            </div>
          ) : (
            <div className="space-y-3">
              {byDay.slice(-14).map((day, idx) => {
                const val = day.count || day.total || 0
                const pct = (val / maxDayValue) * 100
                const lbl = day.date ? new Date(day.date + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', weekday: 'short' }) : `Dia ${idx + 1}`
                return (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 dark:text-gray-400 w-24 text-right flex-shrink-0">{lbl}</span>
                    <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-6 relative overflow-hidden">
                      <div className="h-6 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-700 flex items-center justify-end pr-2" style={{ width: `${Math.max(pct, 2)}%` }}>
                        {pct > 15 && <span className="text-xs font-medium text-white">{val}</span>}
                      </div>
                      {pct <= 15 && <span className="absolute top-1/2 -translate-y-1/2 text-xs font-medium text-gray-600 dark:text-gray-300" style={{ left: `${Math.max(pct, 2) + 2}%` }}>{val}</span>}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    )
  }

  const tabs = [
    { key: 'chat', label: 'Chat', icon: ChatBubbleLeftRightIcon },
    { key: 'templates', label: 'Templates', icon: DocumentTextIcon },
    { key: 'metricas', label: 'Metricas', icon: ChartBarIcon }
  ]

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">WhatsApp Marketing</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Gerencie conversas, templates e acompanhe metricas do WhatsApp
          </p>
        </div>
        {renderConnectionBanner()}
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-1 -mb-px">
          {tabs.map(tab => {
            const Icon = tab.icon
            const isActive = activeTab === tab.key
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      <div>
        {activeTab === 'chat' && renderChatTab()}
        {activeTab === 'templates' && renderTemplatesTab()}
        {activeTab === 'metricas' && renderMetricsTab()}
      </div>
    </div>
  )
}

export default MarketingWhatsappPage
